//libary imports
import { Card, Segmented, Flex, Select, Typography, Empty } from "antd";
import { useEffect, useState, useContext } from "react";
// component imports
import PowerMeter from "../../components/system/power_meter/PowerMeter";
import Inverter from "../../components/system/inverter/Inverter";
import BESS from "../../components/system/bess/BESS";
import LoadUsage from "../../components/system/load_usage/LoadUsage";
import TNB from "../../components/system/tnb/TNB";
import Genset from "../../components/system/genset/Genset";
import WeatherStation from "../../components/system/weather_station/WeatherStation";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";

const { Text } = Typography;

function SystemPage() {
  const [fetchWrapper] = useFetch();
  const {
    siteSelected,
    notificationApi,
    hasInverter,
    hasPvPowerMeter,
    hasGenset,
    hasBess,
    hasTnb,
    hasWeatherStation,
  } = useContext(IndexContext);

  // for system selection
  const systemOptions = [
    ...(hasInverter || hasPvPowerMeter
      ? [{ label: "PV System", value: "pvsystem" }]
      : []),
    { label: "Load Usage", value: "loadusage" },
    ...(hasTnb ? [{ label: "TNB", value: "utility" }] : []),
    ...(hasBess ? [{ label: "BESS", value: "bess" }] : []),
    ...(hasGenset ? [{ label: "Genset", value: "genset" }] : []),
    ...(hasWeatherStation
      ? [{ label: "Weather Station", value: "weatherstation" }]
      : []),
  ];
  const [systemSelection, setSystemSelection] = useState(
    systemOptions[0].value,
  );

  // device phase number list
  const [pvPowerMeterPhaseNumberList, setPvPowerMeterPhaseNumberList] =
    useState([]);
  const [inverterDevicePhaseNumberList, setInverterDevicePhaseNumberList] =
    useState([]);
  const [bessDevicePhaseNumberList, setBessDevicePhaseNumberList] = useState(
    [],
  );
  const [gensetDevicePhaseNumberList, setGensetDevicePhaseNumberList] =
    useState([]);
  const [weatherDevicePhaseNumberList, setWeatherDevicePhaseNumberList] =
    useState([]);

  // for pv system, user can choose to view either power meter or inverter depends on whether the site has power meter or inverter
  // if site has power meter and inverter, user can choose to view either power meter or inverter
  // if site only has inverter, user can only view inverter
  // if site only has power meter, user can only view power meter
  // if site does not have power meter or inverter, 'No PV Device' will be displayed
  const [pvDeviceSelectList, setPvDeviceSelectList] = useState([]);
  const [pvDeviceSelected, setPvDeviceSelected] = useState(null);

  // to determine whether to show card or table view for the data
  const [pvSystemCard, setPvSystemCard] = useState(false);
  const handlePvSystemCardChange = () => {
    setPvSystemCard(!pvSystemCard);
  };

  const [inverterCard, setInverterCard] = useState(false);
  const handleInverterCardChange = () => {
    setInverterCard(!inverterCard);
  };

  const [utilityCard, setUtilityCard] = useState(false);
  const handleUtilityCardChange = () => {
    setUtilityCard(!utilityCard);
  };

  const [bessCard, setBessCard] = useState(false);
  const handleBessCardChange = () => {
    setBessCard(!bessCard);
  };

  const [gensetCard, setGensetCard] = useState(false);
  const handleGensetCardChange = () => {
    setGensetCard(!gensetCard);
  };

  const [weatherCard, setWeatherCard] = useState(false);
  const handleWeatherCardChange = () => {
    setWeatherCard(!weatherCard);
  };

  // open / close state for device information modal
  const [openModal, setOpenModal] = useState(false);

  /** API to get phase number for the device phase number tab */
  function getDevicePhaseNumber() {
    fetchWrapper({
      endpoint_url: "system/getDevicePhaseNumber",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        if (response.data === false) {
          notificationApi.warning({
            message: response.message,
          });
        } else {
          setInverterDevicePhaseNumberList(response.data?.INVERTER ?? []);
          setPvPowerMeterPhaseNumberList(response.data?.PV ?? []);
          setBessDevicePhaseNumberList(response.data?.BESS ?? []);
          setGensetDevicePhaseNumberList(response.data?.GENSET ?? []);
          setWeatherDevicePhaseNumberList(response.data?.WEATHER ?? []);
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get device phase number.",
          description: response.statusText,
        });
      },
    });
  }

  const contentList = {
    powerMeter: (
      <PowerMeter
        devicePhaseNumberList={pvPowerMeterPhaseNumberList}
        pvSystemCard={pvSystemCard}
        handleChange={handlePvSystemCardChange}
        openModal={openModal}
        setOpenModal={setOpenModal}
      />
    ),
    inverter: (
      <Inverter
        devicePhaseNumberList={inverterDevicePhaseNumberList}
        pvSystemCard={inverterCard}
        handleChange={handleInverterCardChange}
        openModal={openModal}
        setOpenModal={setOpenModal}
      />
    ),
    default: <Empty description="No PV Device" />,
  };

  useEffect(() => {
    setInverterDevicePhaseNumberList([]);
    setPvPowerMeterPhaseNumberList([]);
    setBessDevicePhaseNumberList([]);
    setGensetDevicePhaseNumberList([]);

    if (siteSelected) {
      getDevicePhaseNumber();
    }
  }, [siteSelected]);

  useEffect(() => {
    if (hasPvPowerMeter && hasInverter) {
      setPvDeviceSelectList([
        {
          value: "powerMeter",
          label: "Power Meter",
        },
        {
          value: "inverter",
          label: "Inverter",
        },
      ]);
      setPvDeviceSelected("powerMeter");
    } else if (hasPvPowerMeter) {
      setPvDeviceSelectList([
        {
          value: "powerMeter",
          label: "Power Meter",
        },
      ]);
      setPvDeviceSelected("powerMeter");
    } else if (hasInverter) {
      setPvDeviceSelectList([
        {
          value: "inverter",
          label: "Inverter",
        },
      ]);
      setPvDeviceSelected("inverter");
    } else {
      setPvDeviceSelected("default");
    }
  }, [hasPvPowerMeter, hasInverter]);

  return (
    <>
      <Card
        className="h-100"
        title={
          <div className="system-segmented-container">
            <Flex
              className="system-segmented-container-flex"
              justify="space-between"
              align="center"
              gap="small"
            >
              <Segmented
                className="system-segmented"
                size="large"
                options={systemOptions}
                onChange={(value) => {
                  setSystemSelection(value);
                }}
                value={systemSelection}
              />
              {systemSelection == "pvsystem" &&
              (hasInverter == true || hasPvPowerMeter == true) ? (
                <Flex
                  align="center"
                  gap="middle"
                  style={{ marginTop: "5px", marginBottom: "5px" }}
                  wrap="wrap"
                >
                  <Text>Data Select: </Text>
                  <Select
                    style={{ width: 125 }}
                    options={pvDeviceSelectList}
                    value={pvDeviceSelected}
                    onChange={(value) => setPvDeviceSelected(value)}
                  />
                </Flex>
              ) : (
                <></>
              )}
            </Flex>
          </div>
        }
      >
        {systemSelection == "pvsystem" ? (
          contentList[pvDeviceSelected]
        ) : systemSelection == "loadusage" ? (
          <LoadUsage />
        ) : systemSelection == "utility" ? (
          <TNB
            utilityCard={utilityCard}
            handleChange={handleUtilityCardChange}
            openModal={openModal}
            setOpenModal={setOpenModal}
          />
        ) : systemSelection == "bess" ? (
          <BESS devicePhaseNumberList={bessDevicePhaseNumberList} />
        ) : systemSelection == "genset" ? (
          <Genset
            devicePhaseNumberList={gensetDevicePhaseNumberList}
            card={gensetCard}
            handleChange={handleGensetCardChange}
            openModal={openModal}
            setOpenModal={setOpenModal}
          />
        ) : systemSelection == "weatherstation" ? (
          <WeatherStation
            devicePhaseNumberList={weatherDevicePhaseNumberList}
            card={weatherCard}
            handleChange={handleWeatherCardChange}
            openModal={openModal}
            setOpenModal={setOpenModal}
          />
        ) : (
          <></>
        )}
      </Card>
    </>
  );
}

export default SystemPage;
